import { useState, useEffect } from 'react';
import { updateSTMPSetting, testEmail } from '../../../../services/settingService';

interface EmailSettingsProps {
  settings: any;
  onUpdate: (settings: any) => void;
}

const EmailSettings = ({ settings, onUpdate }: EmailSettingsProps) => {
  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    email: '',
    senderName: '',
    username: '',
    password: '',
    useSSL: false
  });
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (settings?.smtp) {
      setFormData({
        host: settings.smtp.host || '',
        port: settings.smtp.port || 587,
        email: settings.smtp.email || '',
        senderName: settings.smtp.senderName || '',
        username: settings.smtp.username || '',
        password: '', // Không hiển thị password cũ
        useSSL: settings.smtp.useSSL || false
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await updateSTMPSetting(formData);
      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Cập nhật thành công' });
        if (onUpdate && response.data) {
          onUpdate(response.data);
        }
        // Reset password field sau khi lưu thành công
        setFormData({ ...formData, password: '' });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await testEmail();
      if (response?.success) {
        setMessage({ type: 'success', text: response.message || 'Đã gửi email thử thành công. Kiểm tra hộp thư.' });
      } else {
        setMessage({ type: 'error', text: response?.message || 'Gửi email thử thất bại' });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Có lỗi khi gửi email thử'
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Cài đặt Email & SMTP</h3>
      
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Host
            </label>
            <input
              type="text"
              placeholder="smtp.gmail.com"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Port
            </label>
            <input
              type="number"
              placeholder="587"
              value={formData.port}
              onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 587 })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email gửi
          </label>
          <input
            type="email"
            placeholder="noreply@stackway.tech"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tên người gửi
          </label>
          <input
            type="text"
            placeholder="StackWay"
            value={formData.senderName}
            onChange={(e) => setFormData({ ...formData, senderName: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password {formData.password === '' && '(Để trống nếu không thay đổi)'}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Nhập password mới để thay đổi"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="smtp-ssl"
            checked={formData.useSSL}
            onChange={(e) => setFormData({ ...formData, useSSL: e.target.checked })}
            className="w-4 h-4 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C]"
          />
          <label htmlFor="smtp-ssl" className="text-sm font-medium text-gray-700">
            Sử dụng SSL/TLS
          </label>
        </div>

        <div className="pt-4 flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            {loading ? 'Đang lưu...' : 'Lưu cài đặt'}
          </button>
          <button
            type="button"
            onClick={handleTestEmail}
            disabled={testLoading}
            className="bg-[#00C897] hover:bg-[#00C897]/90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            {testLoading ? 'Đang gửi...' : 'Test Email'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailSettings;

